import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CompanySearchField from "@/components/CompanySearchField";
import PhotoUploader from "@/components/PhotoUploader";
import PricingEditor from "@/components/PricingEditor";
import AppointmentDateTime from "@/components/AppointmentDateTime";
import { computePricing, lineFromService, lineAmount } from "@/lib/pricing";
import { APPT_STATUS, StatusBadge, contrastText } from "@/components/StatusBadge";
import { ApptBadges } from "@/components/ApptBadges";
import ApptBusinessPanel from "@/components/ApptBusinessPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Loader2, Trash2, Ban, Receipt, Mail, Eye, Download, Printer, CheckCircle2, Wallet, MapPin, Car, Phone, Clock, User, ClipboardList, Pencil, Star, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import InspectionDialog from "@/components/InspectionDialog";
import WorkshopShortcuts from "@/components/workshop/WorkshopShortcuts";
import AssigneeSelect from "@/components/AssigneeSelect";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  format, isSameMonth, isSameDay, eachDayOfInterval, parseISO, setHours, startOfDay, differenceInCalendarDays,
} from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const toLocalInput = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const NEW_VEHICLE = { make: "", model: "", version: "", year: "", plate: "", color: "", mileage: "", vehicle_type: "" };
// Vrai blocage perso = sans client ni prestation ; « Journée entière » avec client reste un RDV (dossier atelier, stock).
const isPersonalBlock = (a) => !!a.is_block && !(a.client_id || (a.line_items || []).length || (a.service_ids || []).length);
const emptyAppt = (start) => ({
  title: "", client_id: "none", vehicle_id: "none", service_ids: [],
  start: toLocalInput(start), end: toLocalInput(addDays(startOfDay(start), 1)),
  status: "planifie", notes: "", is_block: true, all_day: true, before_photos: [], after_photos: [],
  price: "", price_custom: false,
  vehicle_mode: "existing", new_vehicle: { ...NEW_VEHICLE },
  location_type: "mon_local", location_address: "",
  pickup_address: "", pickup_datetime: "", dropoff_address: "", dropoff_datetime: "",
  send_confirmation: false,
  reminder_enabled: false, reminder_offset_hours: 24,
  gcal_sync: true,
  line_items: [], vehicle_category: null, supplements: [], discounts: [],
  travel: { mode: "aucun", distance_km: 0, amount: 0 }, deposit: "",
});

export default function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAppt(new Date()));
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [who, setWho] = useState("");
  const [biz, setBiz] = useState(null);
  const [quoteDraft, setQuoteDraft] = useState(null);
  const quoteReq = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const draftKey = user ? `adm_draft_appt_${user.company_id}_${user.id}` : null;
  const [conflict, setConflict] = useState(null);
  const handleApptClick = (a) => (a._kind === "request" ? navigate("/requests") : openEdit(a));

  const range = (() => {
    if (view === "month") return { start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }) };
    if (view === "week") return { start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
    return { start: startOfDay(cursor), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments", {
        params: { start: addDays(range.start, -1).toISOString(), end: addDays(range.end, 1).toISOString() },
      });
      setAppts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  }, [cursor, view]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/appointment-requests")
      .then((r) => setRequests((r.data || []).filter((x) => ["recue", "date_proposee"].includes(x.status)).map((x) => ({ ...x, _kind: "request", start: x.desired_date || x.created_at, all_day: false }))))
      .catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.active)));
    api.get("/settings").then((r) => setBiz(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (params.get("new")) { openCreate(new Date()); setParams({}); } }, [params]); // eslint-disable-line
  useEffect(() => { const aid = params.get("appt"); if (aid) { api.get(`/appointments/${aid}`).then((r) => { openEdit(r.data); setDialogOpen(true); }).catch(() => {}); setParams({}); } }, [params]); // eslint-disable-line
  useEffect(() => {
    const qid = params.get("from_quote");
    if (!qid || quoteReq.current === qid) return;
    quoteReq.current = qid;
    setParams({});
    api.get(`/quotes/${qid}/appointment-draft`).then(async ({ data: d }) => {
      if (d.existing_appointment_id) { toast.info("Un rendez-vous existe déjà pour ce devis."); const r = await api.get(`/appointments/${d.existing_appointment_id}`); openEdit(r.data); return; }
      const startStr = `${d.chosen_date}T${d.time}`;
      const end = new Date(startStr); end.setHours(end.getHours() + 2);
      openCreate(new Date(startStr), false);
      setQuoteDraft(d);
      await loadVehicles(d.client_id);
      setForm((f) => ({
        ...f, client_id: d.client_id, vehicle_id: d.vehicle_id || "none", title: `Devis ${d.number}`,
        start: startStr, end: toLocalInput(end), is_block: false, all_day: false, status: "planifie", notes: `Référence devis : ${d.number}`,
        line_items: d.items.map((it) => ({ service_id: null, name: it.description || "Prestation", pricing_type: "fixe", non_vehicle: true, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0, vehicle_supplement: 0 })),
        discounts: d.discount > 0 ? [{ label: "Remise devis", amount: d.discount }] : [],
      }));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [params]); // eslint-disable-line
  useEffect(() => {
    if (!draftKey || !dialogOpen || editing) return;
    const t = setTimeout(() => { try { localStorage.setItem(draftKey, JSON.stringify(form)); } catch { /* noop */ } }, 600);
    return () => clearTimeout(t);
  }, [form, dialogOpen, editing, draftKey]);

  const loadVehicles = async (clientId) => {
    if (!clientId || clientId === "none") { setVehicles([]); return; }
    const { data } = await api.get("/vehicles", { params: { client_id: clientId } });
    setVehicles(data);
  };

  const openCreate = (date, block = true) => {
    setEditing(null);
    setQuoteDraft(null);
    setVehicles([]);
    setForm({ ...emptyAppt(date), is_block: block, all_day: block, status: block ? "perso" : "planifie" });
    setDialogOpen(true);
    try {
      if (draftKey && !block) {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && (draft.title || (draft.line_items || []).length || (draft.client_id && draft.client_id !== "none"))) {
            toast("Un rendez-vous non enregistré a été retrouvé.", {
              duration: 12000,
              action: { label: "Reprendre le brouillon", onClick: () => { setForm(draft); if (draft.client_id && draft.client_id !== "none") loadVehicles(draft.client_id); } },
            });
          }
        }
      }
    } catch { /* noop */ }
  };

  const openEdit = async (a) => {
    setEditing(a);
    setQuoteDraft(null);
    await loadVehicles(a.client_id);
    setForm({
      title: a.title || "", client_id: a.client_id || "none", vehicle_id: a.vehicle_id || "none",
      service_ids: a.service_ids || [], start: toLocalInput(parseISO(a.start)), end: toLocalInput(parseISO(a.end)),
      status: a.status, notes: a.notes || "", is_block: !!a.is_block, all_day: !!a.all_day, assignee_id: a.assignee_id || null,
      before_photos: a.before_photos || [], after_photos: a.after_photos || [],
      price: a.price != null ? String(a.price) : "", price_custom: !!a.price_custom,
      vehicle_mode: "existing", new_vehicle: { ...NEW_VEHICLE },
      location_type: a.location_type || "mon_local", location_address: a.location_address || "",
      pickup_address: a.pickup_address || "", pickup_datetime: a.pickup_datetime ? toLocalInput(parseISO(a.pickup_datetime)) : "",
      dropoff_address: a.dropoff_address || "", dropoff_datetime: a.dropoff_datetime ? toLocalInput(parseISO(a.dropoff_datetime)) : "",
      send_confirmation: false,
      reminder_enabled: !!a.reminder_enabled, reminder_offset_hours: a.reminder_offset_hours || 24,
      gcal_sync: a.gcal_sync !== false,
      line_items: (a.line_items && a.line_items.length)
        ? a.line_items
        : (a.service_ids || []).map((sid) => { const s = services.find((x) => x.id === sid); return s ? lineFromService(s, a.vehicle_category) : null; }).filter(Boolean),
      vehicle_category: a.vehicle_category || null,
      supplements: a.supplements || [],
      discounts: a.discounts || [],
      travel: a.travel || { mode: "aucun", distance_km: 0, amount: 0 },
      deposit: a.deposit != null ? String(a.deposit) : "",
    });
    setDialogOpen(true);
  };

  const save = async (overrideStatus, opts = {}) => {
    const isQuick = typeof overrideStatus === "string";
    const status = isQuick ? overrideStatus : form.status;
    const cSel = clients.find((c) => c.id === form.client_id);
    if (!isQuick && form.reminder_enabled && !(cSel && cSel.email)) {
      return toast.error("Adresse e-mail client requise pour activer le rappel e-mail");
    }
    if (!isQuick && status !== "perso" && !opts.ignoreConflict) {
      try {
        const { data: cf } = await api.post("/appointments/check-conflict", {
          start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString(), exclude_id: editing?.id || null, assignee_id: form.assignee_id || null,
        });
        if (cf.has_conflict) { setConflict({ info: cf, overrideStatus, opts }); return; }
      } catch { /* ignore conflict-check errors, never block saving */ }
    }
    setSaving(true);
    let savedId = editing?.id;
    try {
      let vehicleId = form.vehicle_id === "none" ? null : form.vehicle_id;
      // Créer/associer un nouveau véhicule directement depuis le RDV (sans créer de doublon)
      if (form.vehicle_mode === "new" && form.client_id !== "none" && form.new_vehicle?.make?.trim()) {
        const nv = form.new_vehicle;
        const norm = (x) => (x || "").trim().toLowerCase();
        const dup = vehicles.find((v) =>
          (norm(nv.plate) && norm(v.plate) === norm(nv.plate)) ||
          (!norm(nv.plate) && norm(v.make) === norm(nv.make) && norm(v.model) === norm(nv.model))
        );
        if (dup) {
          vehicleId = dup.id;
        } else {
          const { data: v } = await api.post("/vehicles", {
            client_id: form.client_id, make: nv.make.trim(), model: nv.model?.trim() || null,
            plate: nv.plate?.trim() || null, year: nv.year?.trim() || null,
            color: nv.color?.trim() || null, mileage: nv.mileage?.trim() || null,
          });
          vehicleId = v.id;
        }
        await loadVehicles(form.client_id);
      }
      const pricing = computePricing({ line_items: form.line_items, supplements: form.supplements, discounts: form.discounts, travel: form.travel, deposit: form.deposit });
      const derivedServiceIds = (form.line_items || []).map((li) => li.service_id).filter(Boolean);
      const hasLines = (form.line_items || []).length > 0;
      const payload = {
        title: form.title, client_id: form.client_id === "none" ? null : form.client_id, assignee_id: form.assignee_id || null,
        vehicle_id: vehicleId, service_ids: hasLines ? derivedServiceIds : form.service_ids,
        start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString(),
        status: status, notes: form.notes, is_block: form.is_block && form.client_id === "none" && !hasLines && !(form.service_ids || []).length, all_day: form.all_day,
        before_photos: form.before_photos, after_photos: form.after_photos,
        price: hasLines ? pricing.total : (form.price === "" ? null : Number(form.price)), price_custom: !!form.price_custom,
        location_type: form.location_type || "mon_local", location_address: form.location_address || null,
        pickup_address: form.location_type === "recuperation_livraison" ? (form.pickup_address || null) : null,
        pickup_datetime: form.location_type === "recuperation_livraison" && form.pickup_datetime ? new Date(form.pickup_datetime).toISOString() : null,
        dropoff_address: form.location_type === "recuperation_livraison" ? (form.dropoff_address || null) : null,
        dropoff_datetime: form.location_type === "recuperation_livraison" && form.dropoff_datetime ? new Date(form.dropoff_datetime).toISOString() : null,
        reminder_enabled: !!form.reminder_enabled, reminder_offset_hours: form.reminder_offset_hours || 24,
        gcal_sync: form.gcal_sync !== false,
        line_items: (form.line_items || []).map((li) => ({
          service_id: li.service_id || null, name: li.name, pricing_type: li.pricing_type || "fixe",
          non_vehicle: !!li.non_vehicle, quantity: Number(li.quantity) || 1,
          unit_price: (li.unit_price === "" || li.unit_price === null || li.unit_price === undefined) ? null : Number(li.unit_price),
          vehicle_supplement: Number(li.vehicle_supplement) || 0,
          sur_devis_pending: li.pricing_type === "sur_devis" && (li.unit_price === "" || li.unit_price == null),
          description: li.description || null,
        })),
        vehicle_category: form.vehicle_category || null,
        supplements: (form.supplements || []).map((s) => ({ label: s.label || "", quantity: Number(s.quantity) || 1, unit_price: Number(s.unit_price) || 0 })),
        discounts: (form.discounts || []).map((d) => ({ label: d.label || "", amount: Number(d.amount) || 0 })),
        travel: form.travel ? { mode: form.travel.mode || "aucun", distance_km: Number(form.travel.distance_km) || 0, amount: Number(form.travel.amount) || 0 } : null,
        deposit: form.deposit === "" ? 0 : Number(form.deposit),
        pricing_total: hasLines ? pricing.total : null,
        ...(!editing && quoteDraft ? { quote_id: quoteDraft.quote_id } : {}),
      };
      if (editing) await api.put(`/appointments/${editing.id}`, payload);
      else { const { data } = await api.post("/appointments", payload); savedId = data.id; setQuoteDraft(null); }
      if (!isQuick && form.send_confirmation && savedId && cSel && cSel.email) {
        try { await api.post(`/appointments/${savedId}/send-confirmation`, { force: !!editing }); toast.success("Confirmation envoyée au client"); }
        catch (ce) { toast.error("Confirmation non envoyée : " + formatApiError(ce.response?.data?.detail)); }
      }
      try { if (!editing && draftKey) localStorage.removeItem(draftKey); } catch { /* noop */ }
      if (isQuick) setForm((f) => ({ ...f, status }));
      toast.success(editing ? "Rendez-vous mis à jour" : "Rendez-vous créé");
      if (!opts.keepOpen) setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async () => {
    try { await api.delete(`/appointments/${editing.id}`); toast.success("Rendez-vous supprimé"); setDialogOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const apptsOn = (day) => [...appts.filter((a) => !who || a.assignee_id === who), ...(who ? [] : requests)].filter((a) => a.start && apptDays(a).some((d) => isSameDay(d, day)));

  const move = (dir) => {
    if (view === "month") setCursor(addMonths(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addDays(cursor, dir));
  };

  const title = view === "month"
    ? format(cursor, "MMMM yyyy", { locale: fr })
    : view === "week"
      ? `Semaine du ${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM", { locale: fr })}`
      : format(cursor, "EEEE d MMMM yyyy", { locale: fr });

  return (
    <div>
      <PageHeader
        title="Calendrier"
        subtitle="Gérez vos rendez-vous — un véhicule principal par jour"
        icon={CalendarDays}
        actions={
          <>
            <Button variant="outline" onClick={() => openCreate(cursor, true)} data-testid="btn-block-day" className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10"><Ban className="w-4 h-4 mr-1.5" /> Bloquer une journée</Button>
            <Button onClick={() => openCreate(cursor, false)} data-testid="btn-new-appointment" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouveau RDV</Button>
          </>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="btn-prev" className="border-slate-200 bg-white h-9 w-9"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="btn-next" className="border-slate-200 bg-white h-9 w-9"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())} className="border-slate-200 bg-white h-9">Aujourd'hui</Button>
          <span className="font-heading text-lg font-semibold text-slate-900 capitalize ml-2">{title}</span>
        </div>
        <AssigneeSelect value={who} onChange={(v) => setWho(v || "")} testid="cal-filter-assignee" placeholder="Tous les collaborateurs" className="!h-9 !w-auto max-w-[200px] bg-white" />
        <div className="flex bg-white border border-slate-200 rounded-lg p-1">
          {[["month", "Mois"], ["week", "Semaine"], ["day", "Jour"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`} className={`px-3 py-1.5 rounded-md text-sm transition ${view === k ? "bg-emerald-500 text-black font-semibold" : "text-slate-500 hover:text-slate-900"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : view === "month" ? (
        <MonthView cursor={cursor} range={range} apptsOn={apptsOn} onDay={(d) => openCreate(d, true)} onAppt={handleApptClick} />
      ) : view === "week" ? (
        <WeekView cursor={cursor} apptsOn={apptsOn} onDay={(d) => openCreate(d, true)} onAppt={handleApptClick} />
      ) : (
        <DayView cursor={cursor} appts={apptsOn(cursor)} onAppt={handleApptClick} onNew={() => openCreate(cursor, false)} />
      )}

      {!loading && <CalLegend />}

      <ApptDialog
        open={dialogOpen} setOpen={setDialogOpen} editing={editing} form={form} setForm={setForm}
        clients={clients} services={services} vehicles={vehicles} loadVehicles={loadVehicles}
        save={save} del={del} saving={saving} onReload={load} biz={biz} quoteDraft={quoteDraft}
        onClientsChanged={() => api.get("/clients").then((r) => setClients(r.data))}
      />

      <AlertDialog open={!!conflict} onOpenChange={(o) => { if (!o) setConflict(null); }}>
        <AlertDialogContent className="bg-white border-slate-200" data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Conflit de créneau</AlertDialogTitle>
            <AlertDialogDescription>
              {conflict?.info?.conflicts?.length
                ? `Ce créneau entre en conflit avec ${conflict.info.conflicts.length} rendez-vous existant(s).`
                : conflict?.info?.absences?.length ? "" : "Ce créneau entre en conflit avec un événement de votre agenda Google."}
              {conflict?.info?.absences?.length > 0 && <span className="block mt-1 text-amber-700" data-testid="conflict-absence">Absence prévue sur cette période : {conflict.info.absences.map((a) => `${a.start.slice(0, 10)} → ${a.end.slice(0, 10)}${a.reason ? ` (${a.reason})` : ""}${a.user_id ? " — collaborateur attribué" : " — toute l'entreprise"}`).join(", ")}.</span>}
              {" "}Souhaitez-vous tout de même enregistrer ce rendez-vous ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200" data-testid="btn-conflict-cancel">Revoir le créneau</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-conflict-confirm"
              onClick={() => { const c = conflict; setConflict(null); save(c.overrideStatus, { ...c.opts, ignoreConflict: true }); }}
              className="bg-amber-500 hover:bg-amber-600 text-black">Enregistrer quand même</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// La couleur du calendrier est dérivée automatiquement du statut réel du RDV
// (jamais de la mention "PAYÉE" du PDF). Palette + libellés = APPT_STATUS (source unique).
function calState(a) {
  if (a._kind === "request") return "public_attente";
  if (a.status && APPT_STATUS[a.status]) return a.status;
  // Repli pour d'anciens RDV non migrés : un blocage perso sans client -> Perso.
  if (a.is_block && (!a.client_id || a.client_id === "none")) return "perso";
  return "planifie";
}

// Occurrences VISUELLES d'un RDV : un seul RDV en base, affiché sur chaque journée
// comprise entre son début et sa fin. Une fin exactement à minuit est exclusive.
function apptDays(a) {
  if (!a.start) return [];
  const s = parseISO(a.start);
  const e = a.end ? parseISO(a.end) : s;
  let last = startOfDay(e);
  if (last <= startOfDay(s)) return [startOfDay(s)];
  if (e.getHours() === 0 && e.getMinutes() === 0) last = addDays(last, -1);
  if (last < startOfDay(s)) last = startOfDay(s);
  return eachDayOfInterval({ start: startOfDay(s), end: last });
}

function ApptCard({ a, onClick, day }) {
  const st = APPT_STATUS[calState(a)] || APPT_STATUS.planifie;
  const fg = contrastText(st.color);
  const days = apptDays(a);
  const isMulti = days.length > 1;
  const isStart = !day || (days[0] && isSameDay(days[0], day));
  const time = a.all_day ? "Journée" : (a._kind === "request" ? "Public" : format(parseISO(a.start), "HH:mm"));
  const label = a.title || a.client_name || (a._kind === "request" ? `${a.first_name || ""} ${a.last_name || ""}`.trim() : "RDV");
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(a); }} data-testid={`appt-${a.id}`}
      className="w-full min-w-0 overflow-hidden flex items-center gap-1.5 text-left text-[11px] px-1.5 py-1 rounded-md mb-1 shadow-sm hover:brightness-95 transition"
      style={{ background: st.color, color: fg }}>
      <span className="font-mono text-[10px] shrink-0 opacity-80">{isMulti && !isStart ? "→" : time}</span>
      <span className="truncate font-medium">{label || "RDV"}</span>
      <span className="ml-auto"><ApptBadges a={a} compact /></span>
    </button>
  );
}

function CalLegend() {
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-x-4 gap-y-2" data-testid="calendar-legend">
      {Object.entries(APPT_STATUS).map(([k, s]) => (
        <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600" data-testid={`legend-${k}`}>
          <span className="inline-block w-4 h-3 rounded-sm shrink-0 border border-black/10" style={{ background: s.color }} />{s.label}
        </div>
      ))}
    </div>
  );
}

function MobilePill({ a, day, onAppt }) {
  const st = APPT_STATUS[calState(a)] || APPT_STATUS.planifie;
  const days = apptDays(a);
  const isStart = isSameDay(days[0], day);
  const isEnd = isSameDay(days[days.length - 1], day);
  const label = a.client_name || a.title || (a._kind === "request" ? `${a.first_name || ""} ${a.last_name || ""}`.trim() : "RDV");
  const time = !a.all_day && a._kind !== "request" && isStart ? format(parseISO(a.start), "H'h'mm") : "";
  return (
    <button type="button" data-testid={`mpill-${a.id}`} title={label} onClick={(e) => { e.stopPropagation(); onAppt(a); }}
      className={`w-full flex items-start gap-[1px] min-w-0 overflow-hidden text-left text-[9px] leading-[1.1] tracking-tighter px-[2px] py-[2px] mb-[2px] font-semibold ${isStart ? "rounded-l" : "rounded-l-none -ml-0.5 pl-[3px]"} ${isEnd ? "rounded-r" : "rounded-r-none -mr-0.5 pr-[3px]"}`}
      style={{ background: st.color, color: contrastText(st.color), width: `calc(100% + ${(isStart ? 0 : 2) + (isEnd ? 0 : 2)}px)` }}>
      {!isStart && <span className="shrink-0">→</span>}
      {time && <span className="hidden min-[420px]:inline shrink-0 opacity-80 font-mono">{time}</span>}
      <span className="min-w-0 line-clamp-3 [overflow-wrap:anywhere]">{label}</span>
    </button>
  );
}

function DaySheet({ day, list, onClose, onAppt, onNew }) {
  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="day-sheet">
        <DialogHeader><DialogTitle className="text-slate-900 capitalize">{day && format(day, "EEEE d MMMM", { locale: fr })}</DialogTitle><DialogDescription>{list.length} rendez-vous</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {list.map((a) => {
            const st = APPT_STATUS[calState(a)] || APPT_STATUS.planifie;
            const multi = apptDays(a).length > 1;
            const s = parseISO(a.start); const e = a.end ? parseISO(a.end) : s;
            const when = a.all_day ? "Journée entière" : multi ? `${format(s, "d MMM HH:mm", { locale: fr })} → ${format(e, "d MMM HH:mm", { locale: fr })}` : `${format(s, "HH:mm")} – ${format(e, "HH:mm")}`;
            return (
              <button key={a.id} data-testid={`day-sheet-appt-${a.id}`} onClick={() => { onClose(); onAppt(a); }} className="w-full text-left rounded-lg border border-slate-200 p-3 flex gap-3 hover:bg-slate-50">
                <span className="w-1.5 rounded-full shrink-0" style={{ background: st.color }} />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-sm text-slate-900 break-words">{a.client_name || a.title || "RDV"}</span>
                  {a.client_name && a.title && <span className="block text-xs text-slate-500 break-words">{a.title}</span>}
                  <span className="block text-xs text-slate-600 mt-0.5">{when}</span>
                  <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: st.color, color: contrastText(st.color) }}>{st.label}</span>
                </span>
              </button>
            );
          })}
          {!list.length && <p className="text-sm text-slate-400">Aucun rendez-vous ce jour.</p>}
        </div>
        <Button onClick={() => { onClose(); onNew(day); }} data-testid="day-sheet-new" className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" />Nouveau rendez-vous ce jour</Button>
      </DialogContent>
    </Dialog>
  );
}

function MonthView({ cursor, range, apptsOn, onDay, onAppt }) {
  const days = eachDayOfInterval({ start: range.start, end: range.end });
  const weekDays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const [sheetDay, setSheetDay] = useState(null);
  const tapDay = (day) => (window.matchMedia("(max-width: 639px)").matches ? setSheetDay(day) : onDay(day));
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {weekDays.map((d) => <div key={d} className="text-center text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-medium py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const list = apptsOn(day);
          const isToday = isSameDay(day, new Date());
          return (
            <div key={day.toISOString()} onClick={() => tapDay(day)} data-testid={`day-${format(day, "yyyy-MM-dd")}`} className={`min-w-0 overflow-hidden min-h-[76px] sm:min-h-[104px] border-b border-r border-slate-200 p-0.5 sm:p-1.5 cursor-pointer hover:bg-slate-50 transition ${!isSameMonth(day, cursor) ? "opacity-40" : ""}`}>
              <div className={`text-[11px] sm:text-xs mb-1 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full ${isToday ? "bg-emerald-500 text-black font-bold" : "text-slate-500"}`}>{format(day, "d")}</div>
              <div className="sm:hidden">
                {list.slice(0, 2).map((a) => <MobilePill key={a.id} a={a} day={day} onAppt={onAppt} />)}
                {list.length > 2 && <div className="text-[9px] font-semibold text-slate-600 px-0.5" data-testid={`more-${format(day, "yyyy-MM-dd")}`}>+{list.length - 2} autre{list.length - 2 > 1 ? "s" : ""}</div>}
              </div>
              <div className="hidden sm:block">
                {list.slice(0, 3).map((a) => <ApptCard key={a.id} a={a} onClick={onAppt} day={day} />)}
                {list.length > 3 && <div className="text-[10px] text-slate-500 px-1">+{list.length - 3} autre(s)</div>}
              </div>
            </div>
          );
        })}
      </div>
      <DaySheet day={sheetDay} list={sheetDay ? apptsOn(sheetDay) : []} onClose={() => setSheetDay(null)} onAppt={onAppt} onNew={(d) => onDay(d)} />
    </div>
  );
}

function WeekView({ cursor, apptsOn, onDay, onAppt }) {
  const start = startOfWeek(cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {days.map((day) => {
        const list = apptsOn(day);
        const isToday = isSameDay(day, new Date());
        return (
          <div key={day.toISOString()} onClick={() => onDay(day)} className="bg-white border border-slate-200 rounded-xl p-3 min-h-[160px] cursor-pointer hover:border-slate-300">
            <div className={`text-sm font-medium mb-2 capitalize ${isToday ? "text-emerald-600" : "text-slate-700"}`}>{format(day, "EEE d", { locale: fr })}</div>
            {list.length === 0 ? <div className="text-[11px] text-slate-400">—</div> : list.map((a) => <ApptCard key={a.id} a={a} onClick={onAppt} day={day} />)}
          </div>
        );
      })}
    </div>
  );
}

function DayView({ cursor, appts, onAppt, onNew }) {
  const hours = Array.from({ length: 13 }, (_, i) => i + 7); // 7h - 19h
  const isStartDay = (a) => isSameDay(parseISO(a.start), cursor);
  // Les RDV en journée entière + les continuations multi-jours s'affichent en haut.
  const allDayList = appts.filter((a) => a.all_day || !isStartDay(a));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      {appts.length === 0 && <p className="text-sm text-slate-500 mb-4">Aucun rendez-vous ce jour. <button onClick={onNew} className="text-emerald-600 hover:underline">Créer un RDV</button></p>}
      <div className="space-y-1">
        {hours.map((h) => {
          const at = appts.filter((a) => !a.all_day && isStartDay(a) && parseISO(a.start).getHours() === h);
          return (
            <div key={h} className="flex gap-3 border-b border-slate-200 py-2">
              <div className="w-12 text-xs text-slate-500 font-mono pt-1">{String(h).padStart(2, "0")}:00</div>
              <div className="flex-1 space-y-1">{at.map((a) => <ApptCard key={a.id} a={a} onClick={onAppt} day={cursor} />)}</div>
            </div>
          );
        })}
      </div>
      {allDayList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Journée entière</div>
          {allDayList.map((a) => <ApptCard key={a.id} a={a} onClick={onAppt} day={cursor} />)}
        </div>
      )}
    </div>
  );
}

function ApptDialog({ open, setOpen, editing, form, setForm, clients, services, vehicles, loadVehicles, save, del, saving, onClientsChanged, onReload, biz, quoteDraft }) {
  const toggleService = (id) => {
    setForm((f) => {
      const service_ids = f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id];
      const next = { ...f, service_ids };
      // Auto-fill the price from selected services, unless a custom price was set.
      if (!f.price_custom) {
        const total = service_ids.reduce((sum, sid) => sum + (services.find((s) => s.id === sid)?.price || 0), 0);
        next.price = total ? String(total) : "";
      }
      return next;
    });
  };
  const [showNew, setShowNew] = useState(false);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const sendConfirmationNow = async () => {
    try { await api.post(`/appointments/${editing.id}/send-confirmation`, { force: true }); toast.success("Confirmation envoyée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReviewNow = async () => {
    try { const { data } = await api.post(`/appointments/${editing.id}/send-review-request`, { force: true }); toast.success(data.already_sent ? "Demande d'avis déjà envoyée" : "Demande d'avis envoyée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [gcal, setGcal] = useState({ configured: false, connected: false, email: null });
  useEffect(() => {
    if (open && form.status !== "perso") {
      api.get("/oauth/calendar/status").then((r) => setGcal(r.data)).catch(() => {});
    }
  }, [open, form.status]);
  const clientAddr = (clients.find((c) => c.id === form.client_id) || {}).address || "";
  const apptDuration = (form.line_items || []).reduce((sum, li) => { const svc = services.find((s) => s.id === li.service_id); return sum + ((svc?.duration_minutes || 0) * (Number(li.quantity) || 1)); }, 0);
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ client_type: "particulier", first_name: "", last_name: "", company: "", siren: "", siret: "", address: "", address2: "", postal_code: "", city: "", phone: "", email: "", make: "", model: "", plate: "" });
  const clientEmail = showNew ? (nc.email || "").trim() : ((clients.find((c) => c.id === form.client_id) || {}).email || "");
  const [mode, setMode] = useState("view");
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const navigateTo = useNavigate();
  useEffect(() => {
    if (!open) return;
    if (editing) { setMode("view"); setFinalizeOpen(["a_facturer", "en_attente", "paye"].includes(editing.status)); }
    else { setMode("edit"); setFinalizeOpen(false); }
    setShowNew(false);
  }, [open, editing?.id]); // eslint-disable-line
  const startValidation = async () => {
    if (["planifie", "public_attente", "devis"].includes(form.status)) {
      await save("a_facturer", { keepOpen: true });
    }
    setFinalizeOpen(true);
  };

  const createInline = async () => {
    if (nc.client_type === "professionnel" ? !nc.company : !nc.last_name) return toast.error("Nom ou société requis");
    setCreating(true);
    try {
      const fullAddr = [nc.address, nc.address2, [nc.postal_code, nc.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      const { data: c } = await api.post("/clients", { client_type: nc.client_type, first_name: nc.first_name, last_name: nc.last_name, company: nc.company, siren: nc.siren, siret: nc.siret, address: fullAddr, address2: nc.address2, postal_code: nc.postal_code, city: nc.city, phone: nc.phone, email: nc.email });
      let vid = "none";
      if (nc.make) { const { data: v } = await api.post("/vehicles", { client_id: c.id, make: nc.make, model: nc.model, plate: nc.plate }); vid = v.id; }
      await onClientsChanged();
      await loadVehicles(c.id);
      const label = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company;
      setForm((f) => ({ ...f, client_id: c.id, vehicle_id: vid, title: f.title || label }));
      setShowNew(false);
      setNc({ client_type: "particulier", first_name: "", last_name: "", company: "", siren: "", siret: "", address: "", address2: "", postal_code: "", city: "", phone: "", email: "", make: "", model: "", plate: "" });
      toast.success("Client créé et sélectionné");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setCreating(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-slate-200 max-w-lg lg:max-w-[94vw] lg:w-[94vw] 2xl:max-w-[1760px] max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="appt-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">{editing ? (mode === "view" ? "Fiche du rendez-vous" : "Modifier le rendez-vous") : "Nouveau rendez-vous"}</DialogTitle><DialogDescription className="sr-only">Détails, prestations et finalisation du rendez-vous.</DialogDescription></DialogHeader>
        {editing && mode !== "view" && <RecapCard appt={editing} form={form} clients={clients} services={services} vehicles={vehicles} />}
        {!editing && quoteDraft && (
          <div className={`rounded-lg border px-3 py-2 text-sm ${quoteDraft.availability === "available" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="appt-from-quote-banner">
            Rendez-vous créé depuis le devis <b>{quoteDraft.number}</b> — date choisie par le client : <b>{new Date(`${quoteDraft.chosen_date}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</b>.
            {quoteDraft.availability !== "available" && <div className="font-semibold mt-1" data-testid="appt-from-quote-unavailable">Cette date n'est plus disponible dans votre agenda. Choisissez une autre date avant de confirmer.</div>}
            <div className="text-xs mt-1 opacity-80">Vérifiez les informations et l'heure puis enregistrez pour ajouter le rendez-vous à l'agenda.</div>
          </div>
        )}

        {editing && mode === "view" && (
          <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:gap-x-8 lg:gap-y-4 lg:items-start" data-testid="appt-fiche">
            <div className="space-y-3 min-w-0 lg:col-start-1 lg:row-start-1" data-testid="appt-fiche-main">
              <RecapCard appt={editing} form={form} clients={clients} services={services} vehicles={vehicles} />
              <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Statut actuel :</span><StatusBadge status={form.status} />
                {editing.invoice_id && (
                  <Button size="sm" variant="outline" onClick={() => { setOpen(false); navigateTo(`/invoices?focus=${editing.invoice_id}`); }} data-testid="appt-open-invoice" className="ml-auto border-slate-200 h-8"><Receipt className="w-4 h-4 mr-1.5" /> Voir la facture</Button>
                )}
                {editing.quote_id && (
                  <Button size="sm" variant="outline" onClick={() => { setOpen(false); navigateTo(`/quotes?focus=${editing.quote_id}`); }} data-testid="appt-open-quote" className={`${editing.invoice_id ? "" : "ml-auto "}border-slate-200 h-8`}><FileText className="w-4 h-4 mr-1.5" /> Voir le devis</Button>
                )}
              </div>
              <ApptBadges a={editing} />
            </div>
            <div className="space-y-3 min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2" data-testid="appt-fiche-side">
              <ApptBusinessPanel appt={editing} onReload={onReload} />
              {!isPersonalBlock(editing) && <WorkshopShortcuts apptId={editing.id} />}
            </div>
            <div className="space-y-3 min-w-0 lg:col-start-1 lg:row-start-2" data-testid="appt-fiche-finalize">
            {(form.before_photos?.length > 0 || form.after_photos?.length > 0) && (
              <div className="grid grid-cols-2 gap-3" data-testid="fiche-photos">
                <FichePhotos label="AVANT" photos={form.before_photos} />
                <FichePhotos label="APRÈS" photos={form.after_photos} />
              </div>
            )}
            {finalizeOpen ? (
              <FinalizeBlock appt={editing} form={form} services={services} clients={clients} onReload={onReload} />
            ) : (
              <Button onClick={startValidation} disabled={saving} data-testid="btn-validate-prestation" className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-base">
                {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />} Valider la prestation
              </Button>
            )}
            </div>
          </div>
        )}

        <div className="space-y-3 lg:columns-2 lg:gap-10 [&>*]:break-inside-avoid" style={{ display: (!editing || mode === "edit") ? undefined : "none" }}>
          {editing && <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 pt-1">Modifier les informations</div>}
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Titre</Label><Input data-testid="input-appt-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Polissage Golf GTI" className="bg-slate-50 border-slate-200" /></div>

          {showNew ? (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="inline-new-client-panel">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-700">Nouveau client + véhicule</span>
                <button type="button" onClick={() => setShowNew(false)} className="text-[11px] text-slate-500 hover:underline">Choisir un client existant</button>
              </div>
              <div className="flex gap-2">
                {[["particulier", "Particulier"], ["professionnel", "Professionnel"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setNc({ ...nc, client_type: k })} className={`flex-1 py-1.5 rounded-md border text-xs ${nc.client_type === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
                ))}
              </div>
              {nc.client_type === "professionnel" && <CompanySearchField value={nc} onChange={(patch) => setNc({ ...nc, ...patch })} inputClass="bg-white border-slate-200 h-9" testidPrefix="inline-company" />}
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Prénom" value={nc.first_name} onChange={(e) => setNc({ ...nc, first_name: e.target.value })} data-testid="inline-first" className="bg-white border-slate-200 h-9" />
                <Input placeholder={nc.client_type === "professionnel" ? "Nom contact" : "Nom *"} value={nc.last_name} onChange={(e) => setNc({ ...nc, last_name: e.target.value })} data-testid="inline-last" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Téléphone" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} className="bg-white border-slate-200 h-9" />
                <Input placeholder="E-mail" type="email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} data-testid="inline-email" className="bg-white border-slate-200 h-9" />
              </div>
              <Input placeholder="Adresse" value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value })} data-testid="inline-address" className="bg-white border-slate-200 h-9" />
              <Input placeholder="Complément d'adresse (facultatif)" value={nc.address2} onChange={(e) => setNc({ ...nc, address2: e.target.value })} className="bg-white border-slate-200 h-9" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Code postal" value={nc.postal_code} onChange={(e) => setNc({ ...nc, postal_code: e.target.value })} data-testid="inline-postal" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Ville" value={nc.city} onChange={(e) => setNc({ ...nc, city: e.target.value })} data-testid="inline-city" className="bg-white border-slate-200 h-9" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Marque véhicule" value={nc.make} onChange={(e) => setNc({ ...nc, make: e.target.value })} data-testid="inline-make" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Modèle" value={nc.model} onChange={(e) => setNc({ ...nc, model: e.target.value })} className="bg-white border-slate-200 h-9" />
                <Input placeholder="Immatriculation" value={nc.plate} onChange={(e) => setNc({ ...nc, plate: e.target.value })} className="bg-white border-slate-200 h-9" />
              </div>
              <Button type="button" size="sm" onClick={createInline} disabled={creating} data-testid="btn-inline-create" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold w-full">{creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Créer et sélectionner</Button>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label className="text-xs text-slate-500">Client</Label><button type="button" onClick={() => setShowNew(true)} data-testid="btn-inline-new-client" className="text-[11px] text-emerald-600 hover:underline">+ Nouveau</button></div>
              <Select value={form.client_id} onValueChange={(v) => { setForm({ ...form, client_id: v, vehicle_id: "none", vehicle_mode: "existing", new_vehicle: { ...NEW_VEHICLE } }); loadVehicles(v); }}>
                <SelectTrigger data-testid="select-appt-client" className="bg-slate-50 border-slate-200"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700 max-h-56">
                  <SelectItem value="none">Aucun</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">Véhicule</Label>
                {form.client_id !== "none" && (form.vehicle_mode === "new"
                  ? <button type="button" onClick={() => setForm({ ...form, vehicle_mode: "existing" })} className="text-[11px] text-slate-500 hover:underline">Choisir existant</button>
                  : <button type="button" data-testid="btn-inline-new-vehicle" onClick={() => setForm({ ...form, vehicle_mode: "new", vehicle_id: "none" })} className="text-[11px] text-emerald-600 hover:underline">+ Nouveau véhicule</button>)}
              </div>
              {form.vehicle_mode === "new" && form.client_id !== "none" ? (
                <div className="text-[11px] text-emerald-700 bg-emerald-500/5 border border-emerald-500/30 rounded-md px-2 py-2 h-10 flex items-center" data-testid="vehicle-new-hint">Nouveau véhicule ci-dessous ↓</div>
              ) : (
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })} disabled={form.client_id === "none"}>
                <SelectTrigger data-testid="select-appt-vehicle" className="bg-slate-50 border-slate-200"><SelectValue placeholder={form.client_id === "none" ? "Choisir un client" : "Aucun"} /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700 max-h-56">
                  <SelectItem value="none">Aucun</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.make} {v.model} {v.plate ? `· ${v.plate}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              )}
            </div>
          </div>
          {form.vehicle_mode === "new" && form.client_id !== "none" && (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="inline-new-vehicle-panel">
              <span className="text-xs font-semibold text-emerald-700">Nouveau véhicule</span>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Marque *" value={form.new_vehicle.make} onChange={(e) => setForm({ ...form, new_vehicle: { ...form.new_vehicle, make: e.target.value } })} data-testid="nv-make" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Modèle" value={form.new_vehicle.model} onChange={(e) => setForm({ ...form, new_vehicle: { ...form.new_vehicle, model: e.target.value } })} data-testid="nv-model" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Immatriculation" value={form.new_vehicle.plate} onChange={(e) => setForm({ ...form, new_vehicle: { ...form.new_vehicle, plate: e.target.value } })} data-testid="nv-plate" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Année" value={form.new_vehicle.year} onChange={(e) => setForm({ ...form, new_vehicle: { ...form.new_vehicle, year: e.target.value } })} data-testid="nv-year" className="bg-white border-slate-200 h-9" />
                <Input placeholder="Couleur" value={form.new_vehicle.color} onChange={(e) => setForm({ ...form, new_vehicle: { ...form.new_vehicle, color: e.target.value } })} className="bg-white border-slate-200 h-9" />
                <Input placeholder="Kilométrage" value={form.new_vehicle.mileage} onChange={(e) => setForm({ ...form, new_vehicle: { ...form.new_vehicle, mileage: e.target.value } })} className="bg-white border-slate-200 h-9" />
              </div>
              <p className="text-[11px] text-slate-500">Le véhicule sera enregistré dans la fiche du client et associé à ce RDV à la validation.</p>
            </div>
          )}
          </>
          )}

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div><Label className="text-sm text-slate-700">Bloquer la journée entière</Label><p className="text-[11px] text-slate-500">Un véhicule principal par jour</p></div>
            <Switch checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v, is_block: v })} data-testid="switch-all-day" />
          </div>

          <AppointmentDateTime
            start={form.start} end={form.end} allDay={form.all_day} durationMin={apptDuration}
            workStart={biz?.work_start || 7} workEnd={biz?.work_end || 20}
            onChange={({ start, end }) => setForm((f) => ({ ...f, start, end }))}
          />

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Lieu d'intervention</Label>
            <div className="grid grid-cols-3 gap-2">
              {[["mon_local", "À mon local"], ["chez_client", "Chez le client"], ["recuperation_livraison", "Récupération + livraison"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`appt-location-${k}`}
                  onClick={() => setForm({ ...form, location_type: k, location_address: k === "mon_local" ? "" : ((!form.location_address && clientAddr) ? clientAddr : form.location_address), travel: k === "mon_local" ? { mode: "aucun", distance_km: 0, amount: 0 } : form.travel })}
                  className={`py-2 px-1 rounded-lg border text-xs leading-snug transition ${form.location_type === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
            {form.location_type && form.location_type !== "mon_local" && (
              <div className="space-y-1.5 pt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-500">{form.location_type === "recuperation_livraison" ? "Adresse de récupération / livraison" : "Adresse d'intervention"}</Label>
                  {clientAddr && <button type="button" data-testid="appt-location-use-client" onClick={() => setForm({ ...form, location_address: clientAddr })} className="text-[11px] text-emerald-600 hover:underline">Reprendre l'adresse du client</button>}
                </div>
                <Input data-testid="input-appt-location-address" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} placeholder="Adresse complète" className="bg-slate-50 border-slate-200" />
              </div>
            )}
            {form.location_type === "recuperation_livraison" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2" data-testid="pickup-dropoff-fields">
                <div className="space-y-1.5"><Label className="text-xs text-slate-500">Adresse de récupération</Label><Input data-testid="input-pickup-address" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} placeholder={clientAddr || "Adresse"} className="bg-slate-50 border-slate-200" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-slate-500">Date/heure récupération</Label><Input type="datetime-local" data-testid="input-pickup-datetime" value={form.pickup_datetime} onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-slate-500">Adresse de restitution</Label><Input data-testid="input-dropoff-address" value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} placeholder="Peut différer de la récupération" className="bg-slate-50 border-slate-200" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-slate-500">Date/heure restitution</Label><Input type="datetime-local" data-testid="input-dropoff-datetime" value={form.dropoff_datetime} onChange={(e) => setForm({ ...form, dropoff_datetime: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Collaborateur attribué</Label>
            <AssigneeSelect value={form.assignee_id} onChange={(v) => setForm({ ...form, assignee_id: v })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Statut</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="select-appt-status" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-700">
                {Object.entries(APPT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {form.status !== "perso" && (
            <PricingEditor
              value={{ line_items: form.line_items, vehicle_category: form.vehicle_category, supplements: form.supplements, discounts: form.discounts, travel: form.travel, deposit: form.deposit }}
              onChange={(pv) => setForm((f) => ({ ...f, ...pv }))}
              services={services}
              categories={biz?.vehicle_categories || []}
              travelConfig={{ mode: biz?.travel_fee_mode, flat: biz?.travel_flat_amount, perKm: biz?.travel_per_km, roundTrip: biz?.travel_round_trip, freeKm: biz?.travel_free_km, minAmount: biz?.travel_min_amount, tiers: biz?.travel_tiers, recupMultiplier: biz?.recuperation_multiplier }}
              locationType={form.location_type}
              locationAddress={form.location_address}
              computeDistance={async (dest) => { try { const { data } = await api.post("/geo/distance", { destination: dest }); return data; } catch { return { status: "manual_required" }; } }}
            />
          )}

          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Notes</Label><Textarea data-testid="input-appt-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-50 border-slate-200" /></div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="reminder-section">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={!!form.reminder_enabled} disabled={!clientEmail}
                onCheckedChange={(v) => { if (!clientEmail) return; setForm({ ...form, reminder_enabled: !!v }); }}
                data-testid="reminder-enabled" />
              Envoyer un rappel par e-mail
            </label>
            {!clientEmail ? (
              <p className="text-[11px] text-red-600" data-testid="reminder-email-required">Adresse e-mail client requise pour activer le rappel.</p>
            ) : form.reminder_enabled ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  {[[24, "24 h avant"], [48, "48 h avant"]].map(([hh, l]) => (
                    <button key={hh} type="button" data-testid={`reminder-offset-${hh}`} onClick={() => setForm({ ...form, reminder_offset_hours: hh })}
                      className={`flex-1 py-1.5 rounded-md border text-xs transition ${form.reminder_offset_hours === hh ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
                  ))}
                </div>
                {editing?.reminder_sent
                  ? <p className="text-[11px] text-emerald-700 font-medium" data-testid="reminder-status">✓ Rappel e-mail envoyé{editing.reminder_sent_at ? ` le ${new Date(editing.reminder_sent_at).toLocaleString("fr-FR")}` : ""}</p>
                  : <p className="text-[11px] text-slate-500" data-testid="reminder-status">Rappel e-mail activé : {form.reminder_offset_hours === 48 ? "48 h" : "24 h"} avant — envoyé automatiquement à {clientEmail}</p>}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">Le client recevra un e-mail de rappel avant le rendez-vous.</p>
            )}
          </div>

          {form.status !== "perso" && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="confirmation-section">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={!!form.send_confirmation} disabled={!clientEmail}
                  onCheckedChange={(v) => { if (!clientEmail) return; setForm({ ...form, send_confirmation: !!v }); }} data-testid="confirmation-enabled" />
                Envoyer une confirmation par e-mail
              </label>
              {!clientEmail ? (
                <p className="text-[11px] text-red-600">Adresse e-mail client requise pour envoyer la confirmation.</p>
              ) : editing?.confirmation_sent ? (
                <p className="text-[11px] text-emerald-700 font-medium" data-testid="confirmation-status">✓ Confirmation envoyée{editing.confirmation_sent_at ? ` le ${new Date(editing.confirmation_sent_at).toLocaleString("fr-FR")}` : ""}</p>
              ) : (
                <p className="text-[11px] text-slate-500">Le client recevra un récapitulatif complet du rendez-vous.</p>
              )}
            </div>
          )}

          {form.status !== "perso" && gcal.configured && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="gcal-section">
              <label className="flex items-center justify-between gap-2 text-sm text-slate-700">
                <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-emerald-600" /> Synchroniser avec Google Agenda</span>
                <Switch checked={form.gcal_sync !== false && gcal.connected} disabled={!gcal.connected}
                  onCheckedChange={(v) => setForm({ ...form, gcal_sync: !!v })} data-testid="gcal-sync-toggle" />
              </label>
              {gcal.connected ? (
                <p className="text-[11px] text-slate-500" data-testid="gcal-status">
                  {form.gcal_sync !== false
                    ? `Ce rendez-vous sera ajouté à votre agenda Google (${gcal.email || "connecté"}).`
                    : "Ce rendez-vous ne sera pas synchronisé."}
                </p>
              ) : (
                <p className="text-[11px] text-slate-400" data-testid="gcal-status">Connectez votre agenda Google dans Paramètres → Compte pour activer la synchronisation.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PhotoUploader label="Photos AVANT" value={form.before_photos} onChange={(p) => setForm({ ...form, before_photos: p })} testid="before-photo" />
            <PhotoUploader label="Photos APRÈS" value={form.after_photos} onChange={(p) => setForm({ ...form, after_photos: p })} testid="after-photo" />
          </div>

          {!editing && (
            <div className="pt-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Récapitulatif avant validation</div>
              <RecapCard appt={{}} form={form} clients={clients} services={services} vehicles={vehicles} />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:flex-wrap gap-2">
          {editing && mode === "view" ? (
            <>
              <div className="flex gap-2 sm:mr-auto">
                <Button variant="outline" data-testid="btn-status-annule" onClick={() => save("annule")} disabled={saving} className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10"><Ban className="w-4 h-4 mr-1.5" />Annulé</Button>
                <Button variant="outline" data-testid="btn-status-absent" onClick={() => save("absent")} disabled={saving} className="border-amber-500/30 bg-amber-500/5 text-amber-700 hover:bg-amber-500/10">Absent</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" data-testid="btn-send-confirmation" onClick={sendConfirmationNow} className="border-slate-200"><Mail className="w-4 h-4 mr-1.5" />Confirmation</Button>
                <Button variant="outline" size="sm" data-testid="btn-send-review" onClick={sendReviewNow} className="border-slate-200"><Star className="w-4 h-4 mr-1.5" />Demander un avis</Button>
                <Button variant="outline" size="sm" data-testid="btn-open-inspection" onClick={() => setInspectionOpen(true)} className="border-slate-200"><ClipboardList className="w-4 h-4 mr-1.5" />État des lieux</Button>
              </div>
              <Button variant="ghost" onClick={() => setOpen(false)}>Fermer</Button>
              <Button variant="outline" data-testid="btn-edit-appt" onClick={() => setMode("edit")} className="border-slate-200"><Pencil className="w-4 h-4 mr-1.5" />Modifier</Button>
            </>
          ) : (
            <>
              {editing && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" data-testid="btn-delete-appt" className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10 sm:mr-auto"><Trash2 className="w-4 h-4 mr-1.5" /> Supprimer</Button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-white border-slate-200">
                    <AlertDialogHeader><AlertDialogTitle className="text-slate-900">Supprimer ce rendez-vous ?</AlertDialogTitle><AlertDialogDescription>Action irréversible.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-slate-200">Annuler</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600" data-testid="btn-confirm-delete-appt">Supprimer</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button variant="ghost" onClick={() => (editing ? setMode("view") : setOpen(false))}>Annuler</Button>
              <Button onClick={save} disabled={saving} data-testid="btn-save-appt" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
      {editing && (
        <InspectionDialog open={inspectionOpen} setOpen={setInspectionOpen}
          appointmentId={editing.id} vehicleId={form.vehicle_id !== "none" ? form.vehicle_id : (editing.vehicle_id || null)}
          clientId={form.client_id !== "none" ? form.client_id : null} onSaved={onReload} />
      )}
    </>
  );
}

const PAY_METHODS = [
  ["espece", "Espèces"], ["carte", "Carte bancaire"], ["virement", "Virement"], ["cheque", "Chèque"], ["autre", "Autre"],
];
const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

function FichePhotos({ label, photos = [] }) {
  if (!photos.length) return <div className="text-[11px] text-slate-400">Aucune photo {label.toLowerCase()}</div>;
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {photos.map((p, i) => <img key={i} src={fileSrc(p.url || p.path || p)} alt="" className="w-14 h-14 rounded-md object-cover border border-slate-200" />)}
      </div>
    </div>
  );
}

function FinalizeBlock({ appt, form, services, clients, onReload }) {
  const client = clients.find((c) => c.id === form.client_id) || {};
  const servicesTotal = (form.service_ids || []).reduce((sum, id) => {
    const s = services.find((x) => x.id === id);
    return sum + (s?.price || 0);
  }, 0);
  // Prefer the price saved on the RDV; fall back to services total for older RDV.
  const storedPrice = form.price !== "" && form.price != null ? Number(form.price)
    : (appt?.price != null ? Number(appt.price) : null);
  const prc = computePricing({ line_items: form.line_items || [], supplements: form.supplements || [], discounts: form.discounts || [], travel: form.travel, deposit: form.deposit });
  const defaultAmount = (form.line_items || []).length ? prc.total
    : (storedPrice != null && !Number.isNaN(storedPrice) ? storedPrice : servicesTotal);

  const [invoice, setInvoice] = useState(null);
  const [amount, setAmount] = useState(defaultAmount ? String(defaultAmount) : "");
  const [method, setMethod] = useState("espece");
  const [received, setReceived] = useState(false);
  const [email, setEmail] = useState(client.email || "");
  const [saveEmail, setSaveEmail] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sendToClient, setSendToClient] = useState(true);
  const [copyOwner, setCopyOwner] = useState(true);
  const [sending, setSending] = useState(false);
  const [justCreated, setJustCreated] = useState(false);

  useEffect(() => {
    if (appt?.invoice_id) {
      api.get(`/invoices/${appt.invoice_id}`).then((r) => { setInvoice(r.data); setEmail((e) => e || client.email || ""); }).catch(() => {});
    }
  }, [appt?.invoice_id]); // eslint-disable-line

  const vehicleName = (() => {
    const v = form.vehicle_id;
    return v && v !== "none" ? "" : "";
  })();

  const create = async () => {
    if (!form.client_id || form.client_id === "none") return toast.error("Sélectionnez un client pour facturer");
    if (!amount || Number(amount) <= 0) return toast.error("Renseignez un montant TTC valide");
    setCreating(true);
    try {
      const { data } = await api.post(`/appointments/${appt.id}/finalize`, {
        amount: Number(amount), payment_method: method, payment_status: received ? "paye" : "a_payer",
        payment_received: received,
        client_email: email || null, save_email_to_client: saveEmail,
      });
      setInvoice(data);
      setJustCreated(!data.already_existed);
      toast.success(data.already_existed ? "Une facture existait déjà pour ce RDV" : "Facture créée ✓");
      onReload?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setCreating(false); }
  };

  const pdfUrl = invoice ? fileSrc(`/api/invoices/${invoice.id}/pdf`) : "";
  const openPdf = () => window.open(pdfUrl, "_blank");
  const printPdf = () => { const w = window.open(pdfUrl, "_blank"); if (w) w.addEventListener("load", () => { try { w.print(); } catch (e) {} }); };
  const download = async () => {
    try {
      const res = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${invoice.number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { window.open(pdfUrl, "_blank"); }
  };
  const send = async () => {
    if (!sendToClient && !copyOwner) return toast.error("Choisissez au moins un destinataire");
    if (sendToClient && !email) return toast.error("Renseignez l'email du client");
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/send`, {
        to_email: email || null, send_to_client: sendToClient, copy_to_owner: copyOwner, save_email_to_client: saveEmail,
      });
      toast.success(data.sent_to_client ? "Facture envoyée par email ✓" : "Copie envoyée à votre email pro ✓");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/[0.04] p-4 space-y-3" data-testid="finalize-block">
      <div className="flex items-center gap-2 text-emerald-700 font-heading font-bold">
        <Wallet className="w-5 h-5" /> FINALISER LA PRESTATION
      </div>

      {!invoice ? (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[11px] text-slate-500">Client</div><div className="font-medium text-slate-900">{client.first_name || client.last_name ? `${client.first_name || ""} ${client.last_name || ""}`.trim() : (client.company || "—")}</div></div>
            <div><div className="text-[11px] text-slate-500">Date</div><div className="font-medium text-slate-900">{form.start ? new Date(form.start).toLocaleDateString("fr-FR") : "—"}</div></div>
            <div className="col-span-2"><div className="text-[11px] text-slate-500">Prestation(s)</div><div className="font-medium text-slate-900">{(form.service_ids || []).map((id) => services.find((s) => s.id === id)?.name).filter(Boolean).join(", ") || form.title || "Prestation detailing"}</div></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Montant TTC (€) — modifiable</Label>
              <Input type="number" step="0.01" data-testid="finalize-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="bg-white border-slate-200 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Mode de paiement</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="finalize-method" className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700">
                  {PAY_METHODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <Label className="text-xs font-semibold text-slate-700">Paiement réellement reçu ?</Label>
            <div className="flex gap-2">
              {[[true, "OUI 🟢 reçu"], [false, "NON 🔵 en attente"]].map(([k, l]) => (
                <button key={String(k)} type="button" onClick={() => setReceived(k)} data-testid={`finalize-received-${k ? "yes" : "no"}`}
                  className={`flex-1 py-2 rounded-lg border text-sm transition ${received === k ? "bg-slate-900 text-white border-slate-900 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">Le statut du RDV et la facture suivent automatiquement : « OUI » → <b>Payé</b> (facture PAYÉE) · « NON » → <b>En attente de règlement</b>.</p>
          </div>

          {!client.email && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Email du client (aucun email enregistré)</Label>
              <Input type="email" data-testid="finalize-email-missing" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className="bg-white border-slate-200" />
              <label className="flex items-center gap-2 text-[11px] text-slate-600"><Checkbox checked={saveEmail} onCheckedChange={setSaveEmail} data-testid="finalize-save-email" /> Enregistrer cet email dans la fiche client</label>
            </div>
          )}

          <Button onClick={create} disabled={creating} data-testid="btn-create-invoice" className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-base">
            {creating ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Receipt className="w-5 h-5 mr-2" />} CRÉER LA FACTURE
          </Button>
        </>
      ) : (
        <div className="space-y-3" data-testid="finalize-invoice-ready">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <CheckCircle2 className="w-5 h-5" /> {justCreated ? "Facture créée ✓" : "Facture existante"} — <span className="font-mono" data-testid="finalize-invoice-number">n° {invoice.number}</span>
          </div>
          <div className="text-sm text-slate-600">Montant : <span className="font-mono font-semibold text-slate-900">{eur(invoice.total)}</span></div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Email du client</Label>
            <Input type="email" data-testid="finalize-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className="bg-white border-slate-200" />
            {!client.email && <label className="flex items-center gap-2 text-[11px] text-slate-600"><Checkbox checked={saveEmail} onCheckedChange={setSaveEmail} data-testid="finalize-save-email-2" /> Enregistrer cet email dans la fiche client</label>}
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={sendToClient} onCheckedChange={setSendToClient} data-testid="finalize-send-client" /> Envoyer la facture PDF au client</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={copyOwner} onCheckedChange={setCopyOwner} data-testid="finalize-copy-owner" /> Envoyer une copie à mon email professionnel</label>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Button onClick={send} disabled={sending} data-testid="btn-send-invoice" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold col-span-2 sm:col-span-1">{sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Mail className="w-4 h-4 mr-1.5" />} Envoyer</Button>
            <Button variant="outline" onClick={openPdf} data-testid="btn-view-invoice" className="border-slate-200"><Eye className="w-4 h-4 mr-1.5" /> Voir</Button>
            <Button variant="outline" onClick={download} data-testid="btn-download-invoice" className="border-slate-200"><Download className="w-4 h-4 mr-1.5" /> Télécharger</Button>
            <Button variant="outline" onClick={printPdf} data-testid="btn-print-invoice" className="border-slate-200"><Printer className="w-4 h-4 mr-1.5" /> Imprimer</Button>
          </div>
          <p className="text-[11px] text-slate-400">Vous pouvez aussi retrouver et renvoyer cette facture depuis le module Factures.</p>
        </div>
      )}
    </div>
  );
}


// Récapitulatif clair et structuré affiché en haut de la fiche RDV (lecture rapide),
// avant le formulaire d'édition et le bloc de finalisation. N'enlève aucune fonction.
function RecapCard({ appt, form, clients, services, vehicles = [] }) {
  const client = clients.find((c) => c.id === form.client_id) || {};
  const vehicleLabel = form.vehicle_mode === "new"
    ? [form.new_vehicle?.make, form.new_vehicle?.model, form.new_vehicle?.plate ? `· ${form.new_vehicle.plate}` : ""].filter(Boolean).join(" ").trim()
    : (form.vehicle_id && form.vehicle_id !== "none"
        ? (() => { const v = vehicles.find((x) => x.id === form.vehicle_id); return v ? `${v.make} ${v.model || ""} ${v.plate ? "· " + v.plate : ""}`.trim() : (appt.vehicle_name || ""); })()
        : (appt.vehicle_name || ""));
  const clientName = (client.first_name || client.last_name)
    ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
    : (client.company || appt.client_name || "");
  const hasLines = (form.line_items || []).length > 0;
  const pr = computePricing({ line_items: form.line_items || [], supplements: form.supplements || [], discounts: form.discounts || [], travel: form.travel, deposit: form.deposit });
  const sel = (form.service_ids || []).map((id) => services.find((s) => s.id === id)).filter(Boolean);
  const svcSum = sel.reduce((s, x) => s + (x.price || 0), 0);
  const priceSet = form.price !== "" && form.price != null;
  const total = hasLines ? pr.total : (priceSet ? Number(form.price) : svcSum);
  const hasCustom = !hasLines && priceSet && Number(form.price) !== svcSum;
  const start = form.start ? new Date(form.start) : null;
  const end = form.end ? new Date(form.end) : null;
  const LOC = { mon_local: "À mon local", chez_client: "Chez le client", recuperation_livraison: "Récupération + livraison" };
  const locLabel = LOC[form.location_type] || null;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3" data-testid="appt-recap">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-bold text-slate-900 text-base break-words">{form.title || clientName || "Rendez-vous"}</div>
          {clientName && form.title && <div className="text-xs text-slate-500 break-words">{clientName}</div>}
        </div>
        <StatusBadge status={form.status} />
      </div>

      <div className="grid grid-cols-1 gap-1.5 text-sm" data-testid="appt-recap-details">
        <div className="flex items-center gap-2 text-slate-700"><CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />{start ? start.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "—"}</div>
        <div className="flex items-center gap-2 text-slate-700"><Clock className="w-4 h-4 text-slate-400 shrink-0" />{form.all_day ? "Journée entière" : (start && end ? `${start.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} → ${end.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "—")}</div>
        {vehicleLabel && <div className="flex items-center gap-2 text-slate-700" data-testid="appt-recap-vehicle"><Car className="w-4 h-4 text-slate-400 shrink-0" />{vehicleLabel}</div>}
        {locLabel && <div className="flex items-start gap-2 text-slate-700" data-testid="appt-recap-location"><MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span className="break-words">{locLabel}{form.location_type !== "mon_local" && form.location_address ? ` · ${form.location_address}` : ""}</span></div>}
        {client.address && <div className="flex items-start gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span className="break-words">{client.address}</span></div>}
        {!hasLines && sel.length === 0 && total > 0 && <div className="flex items-center gap-2 text-slate-700" data-testid="appt-recap-amount"><Receipt className="w-4 h-4 text-slate-400 shrink-0" /><span className="font-mono font-semibold text-slate-900">{eur(total)}</span></div>}
      </div>

      {hasLines && <PricingRecap form={form} pr={pr} />}

      {(clientName || client.phone || client.email) && (
        <div className="rounded-lg bg-white border border-slate-200 p-3 space-y-1.5" data-testid="appt-recap-client">
          <div className="flex items-center gap-2 flex-wrap">
            <User className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="font-medium text-slate-900 break-words">{clientName || "Client"}</span>
            {client.client_type && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{client.client_type === "professionnel" ? "Pro" : "Particulier"}</span>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {client.phone && <a href={`tel:${client.phone}`} className="flex items-center gap-1 text-emerald-600 hover:underline" data-testid="appt-recap-phone"><Phone className="w-3 h-3" />{client.phone}</a>}
            {client.email && <a href={`mailto:${client.email}`} className="flex items-center gap-1 text-emerald-600 hover:underline break-all" data-testid="appt-recap-email"><Mail className="w-3 h-3" />{client.email}</a>}
          </div>
        </div>
      )}

      {!hasLines && sel.length > 0 && (
        <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="appt-recap-services">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2"><ClipboardList className="w-4 h-4" /> Prestations</div>
          <div className="space-y-2">
            {sel.map((s) => (
              <div key={s.id} className="flex items-center gap-2.5 text-sm" data-testid={`recap-service-${s.id}`}>
                <span className="w-1.5 h-5 rounded-full shrink-0" style={{ background: s.color || "#e2e8f0" }} />
                <span className="flex-1 text-slate-800 break-words leading-snug">{s.name}</span>
                {s.price != null && <span className="font-mono text-slate-600 shrink-0">{eur(s.price)}</span>}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 mt-2.5 pt-2.5">
            <span className="text-sm font-semibold text-slate-700">TOTAL{hasCustom ? " · tarif du RDV" : ""}</span>
            <span className="font-mono font-bold text-slate-900" data-testid="appt-recap-total">{eur(total)}</span>
          </div>
        </div>
      )}

      {form.notes && (
        <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="appt-recap-notes">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Notes</div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{form.notes}</p>
        </div>
      )}

      {form.reminder_enabled && (
        <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="appt-recap-reminder">
          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span>Rappel e-mail {form.reminder_offset_hours === 48 ? "48 h" : "24 h"} avant{client.email ? ` · ${client.email}` : ""}{appt.reminder_sent ? " — envoyé ✓" : ""}</span>
        </div>
      )}
    </div>
  );
}

// Détail tarifaire (lecture seule) affiché dans la fiche quand un snapshot existe.
function PricingRecap({ form, pr }) {
  const lines = form.line_items || [];
  const extras = (form.supplements || []).filter((s) => (Number(s.unit_price) || 0) > 0);
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3 space-y-2" data-testid="appt-recap-pricing">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><ClipboardList className="w-4 h-4" /> Prestations & tarifs</div>
      <div className="space-y-1.5">
        {lines.map((li, i) => {
          const amt = lineAmount(li);
          const unit = (Number(li.unit_price) || 0) + (li.non_vehicle ? 0 : (Number(li.vehicle_supplement) || 0));
          return (
            <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-0.5 text-sm" data-testid={`recap-line-${i}`}>
              <span className="flex-1 min-w-0 text-slate-800 break-words leading-snug">{li.name}</span>
              <span className="font-mono text-slate-600 shrink-0 ml-auto text-xs sm:text-sm whitespace-nowrap">{amt == null ? "Sur devis" : `${li.quantity} × ${eur(unit)} = ${eur(amt)}`}</span>
            </div>
          );
        })}
      </div>
      {extras.length > 0 && (
        <div className="border-t border-slate-100 pt-1.5 space-y-1">
          {extras.map((s, i) => (
            <div key={i} className="flex items-center justify-between text-sm text-slate-600"><span>{s.label || "Supplément"} × {s.quantity || 1}</span><span className="font-mono">{eur((Number(s.quantity) || 1) * (Number(s.unit_price) || 0))}</span></div>
          ))}
        </div>
      )}
      {pr.travelAmount > 0 && <div className="flex items-center justify-between text-sm text-slate-600"><span>Déplacement</span><span className="font-mono">{eur(pr.travelAmount)}</span></div>}
      {pr.discountTotal > 0 && <div className="flex items-center justify-between text-sm text-slate-600"><span>Réductions</span><span className="font-mono text-red-600">−{eur(pr.discountTotal)}</span></div>}
      <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-1">
        <span className="text-sm font-semibold text-slate-700">TOTAL</span>
        <span className="font-mono font-bold text-slate-900" data-testid="appt-recap-total">{eur(pr.total)}</span>
      </div>
      {(Number(form.deposit) || 0) > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-500"><span>Acompte : {eur(form.deposit)}</span><span className="font-semibold text-slate-700">Reste à payer : <span className="font-mono">{eur(pr.remaining)}</span></span></div>
      )}
      {pr.hasPending && <p className="text-[11px] text-amber-600">Une prestation « sur devis » reste à chiffrer.</p>}
    </div>
  );
}
